import { Box, Button, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import { mealPlanMolecule } from '@web-app/atoms/charting/mealPlanMolecule';
import { useAutoGenerateInstructions } from '@web-app/hooks/useAutoGenerateInstructions';
import { useMolecule } from 'bunshi/react';
import { useAtomValue } from 'jotai';
import { FC } from 'react';
import { LuChefHat } from 'react-icons/lu';

import { PrepStepHeader } from './PrepStepHeader';
import { SessionPrepCardSkeleton } from './SessionPrepCardSkeleton';
import { useMealPlanCardStatus } from './useMealPlanCardStatus';

export interface MealPlanCardProps {
  stepNumber?: number;
  onOpen: () => void;
}

function getButtonLabel(status: string, isStandalonePageOpen: boolean): string {
  if (status === 'sent') return 'View';
  if (isStandalonePageOpen) return 'Open';
  if (status === 'idle') return 'Start now';
  return 'Continue';
}

export const MealPlanCard: FC<MealPlanCardProps> = ({ stepNumber, onOpen }) => {
  const { status, label } = useMealPlanCardStatus();
  const { isReady } = useAutoGenerateInstructions();
  const { isStandalonePageOpenAtom } = useMolecule(mealPlanMolecule);
  const isStandalonePageOpen = useAtomValue(isStandalonePageOpenAtom);

  const buttonLabel = getButtonLabel(status, isStandalonePageOpen);
  const showTwoLines = isStandalonePageOpen && status !== 'sent';

  if (!isReady) {
    return <SessionPrepCardSkeleton data-testid="meal-plan-card-skeleton" />;
  }

  return (
    <VStack spacing={2} align="stretch">
      {stepNumber !== undefined && (
        <PrepStepHeader
          stepNumber={stepNumber}
          title="Create a meal plan"
          description="Generate a personalized meal plan to send to your patient"
        />
      )}
      <Box
        bg="bg.default"
        borderRadius="2xl"
        border="1px solid"
        borderColor="border.default"
        p={5}
        data-testid="meal-plan-card"
      >
        <VStack spacing={1} align="stretch">
          <HStack spacing={3} align="center">
            <Icon
              as={LuChefHat}
              boxSize="26px"
              color="interactive.brand.default"
              opacity={0.6}
            />
            <Text flex={1} fontSize="sm" fontWeight="600">
              {showTwoLines
                ? 'You are building a meal plan in a different window'
                : label}
            </Text>
            <Button size="xs" onClick={onOpen}>
              {buttonLabel}
            </Button>
          </HStack>
          {showTwoLines && (
            <Text fontSize="xs" color="text.muted" pl="38px">
              {label}
            </Text>
          )}
        </VStack>
      </Box>
    </VStack>
  );
};

MealPlanCard.displayName = 'MealPlanCard';
